import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { Form, Formik } from 'formik';
import CommonButton from '../../common/CommonButton';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useState } from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { addNewAdministrationItem } from '../../../actions/administration/administration-actions';
import AdministrationItemsFormFields, {
  ADMINISTRATION_DATA_TYPE,
  getDefaultValueForFieldType,
  sliceRows,
} from './AdministrationItemsFormFields';
import PropTypes from 'prop-types';
import Switch from '@material-ui/core/Switch/Switch';
import Box from '@material-ui/core/Box';
import CustomPrompt from '../../common/CustomPrompt';
import { BACKGROUND_DARK, DARK_UPDATED_FIELD, LIGHT_UPDATED_FIELD } from '../../../const/color-const';

const RADIUS = 4;
const INPUT_HEIGHT = 56;
const BORDER_COLOR_DARK = '#535761';
const BOX_SHADOW = 'rgba(180, 180, 180, 0.5) 0px 2px 2px 1px';
const DISABLED_LIGHT = '#e8e8e8';
const DISABLED_DARK = '#1f2432';

export const fieldCustomStyles = theme => ({
  boxShadow: theme.palette.type === 'light' ? BOX_SHADOW : `unset`,
  border: theme.palette.type === 'light' ? 'unset' : `1px solid ${BORDER_COLOR_DARK}`,
  background: theme.palette.type === 'light' ? '#f9f9f9' : BACKGROUND_DARK,
  borderRadius: RADIUS,
  height: INPUT_HEIGHT,
  maxWidth: '87%',
  [theme.breakpoints.up('md')]: {
    maxWidth: '240px',
  },
  '& .Mui-disabled': {
    cursor: 'not-allowed',
  },
});

export const sectionWrapperStyles = theme => ({
  sectionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      marginBottom: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: 'row',
    },
    '& > div': {
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(0.3),
      [theme.breakpoints.up('md')]: {
        width: 250,
      },
      '& svg[class*="tooltipIcon"]': {
        right: 12,
        top: -10,
      },
      '& .isDisabled': {
        cursor: 'not-allowed',
        background: theme.palette.type === 'light' ? DISABLED_LIGHT : DISABLED_DARK,
        boxShadow: 'none',
        border: 'none',
      },
      '& #colorPicker': {
        width: '100%',
        ...fieldCustomStyles(theme),
        margin: theme.spacing(1.5, 1, 1.5, 1),
        [theme.breakpoints.up('md')]: {
          maxWidth: '220px',
        },
        '& label': {
          top: -14,
        },
        '& > div': {
          width: 240,
          [theme.breakpoints.down('md')]: {
            width: '100%',
          },
        },
      },
      '& div[class*="pickerWrapper"], #colorPicker': {
        '& label': {
          display: 'flex',
          marginLeft: theme.spacing(1.5),
          top: -4,
        },
        '& [class*="MuiInputBase-formControl"]': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 0,
          '&:before, &:after': {
            display: 'none',
          },
        },
      },
    },
    '& div[class*="accountNumberWrapper"] + div': {
      '& svg[class*="tooltipIcon"]': {
        right: 6,
      },
    },
    '& [class*="dateTimePickerWrapper"]': {
      margin: 0,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& [class*="MultiSelectField"]': {
      ...fieldCustomStyles(theme),
      '& fieldset': {
        border: 'none',
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: 220,
        margin: 0,
      },
    },
    '& [class*="MuiTextField"]': {
      ...fieldCustomStyles(theme),
      margin: theme.spacing(1.5, 0, 1.5, 0),
      width: '100%',
      [theme.breakpoints.up('md')]: {
        margin: theme.spacing(1.5, 1, 1.5, 1),
        width: 220,
      },
      '& button': {
        position: 'relative',
        bottom: 5,
        right: 6,
      },
      '& label': {
        padding: theme.spacing(0, 1, 0, 1.5),
        top: -5,
      },
      '& label[class*="Mui-focused"], label[class*="MuiFormLabel-filled"]': {
        top: 4,
      },
      '& p.Mui-error': {
        position: 'absolute',
        bottom: -20,
        left: 10,
      },
      '& p:not(.Mui-error)': {
        position: 'relative',
        bottom: 6,
        maxWidth: 220,
        whiteSpace: 'nowrap',
      },
      '& > div': {
        width: '100%',
        paddingLeft: theme.spacing(1),
        '& input': {
          textOverflow: 'ellipsis',
          height: '100%',
          marginBottom: 0,
          marginTop: 0,
        },
        '&:before, &:after': {
          display: 'none',
        },
      },
    },
    '& div[class*="MuiTextField"]:has([class*="MuiFormHelperText"]), & div[class*="commonSelectField"]:has([class*="MuiFormHelperText"])': {
      marginBottom: theme.spacing(2),
    },
    '& div[class*="MuiTextField"] > p[class*="MuiFormHelperText"], & div[class*="commonSelectField"] > p[class*="MuiFormHelperText"]': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      position: 'absolute',
      overflow: 'hidden',
      maxWidth: 280,
      bottom: -22,
      left: 0,
    },
    '& [class*="CommonCheckboxWrapper"]': {
      margin: theme.spacing(1.5, 1, 1.5, 1),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'initial',
      [theme.breakpoints.up('md')]: {
        height: INPUT_HEIGHT,
      },
      '& .Mui-disabled': {
        cursor: 'not-allowed',
      },
      '& label': {
        margin: 0,
        [theme.breakpoints.down('md')]: {
          display: 'flex',
          justifyContent: 'center',
        },
      },
    },
    '& .commonSelectField': {
      ...fieldCustomStyles(theme),
      '& [class*="selectField"]': {
        '& > div': {
          marginTop: theme.spacing(1),
        },
      },
      margin: theme.spacing(1.5, 1, 1.5, 1),
      '& label': {
        height: 20,
      },
      '& label[class*="MuiFormLabel-filled"], & label[class*="Mui-focused"]': {
        top: 10,
      },
      '& fieldset': {
        border: 'none',
      },
      '& p.Mui-error': {
        position: 'absolute',
        bottom: -20,
        left: 10,
      },
    },
    '& [class*="isUpdated"]': {
      backgroundColor: (theme.palette.type === 'light' ? LIGHT_UPDATED_FIELD : DARK_UPDATED_FIELD) + ' !important',
    },
  },
});

const styles = theme => ({
  dialogWrapper: {
    overflow: 'visible',
    '& [class*="MuiDialog-container"]': {
      '& > div': {
        overflowY: 'scroll',
        overflowX: 'visible',
        background: theme.palette.type === 'light' ? '#fff' : BACKGROUND_DARK,
        [theme.breakpoints.down('md')]: {
          maxWidth: '95%',
          width: '100%',
        },
        '& #form-dialog-title': {
          paddingBottom: 0,
        },
      },
    },
  },
  dialogContent: {
    overflow: 'visible',
  },
  advOptions: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(0, 3, 2, 3),
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
    '& button': {
      width: 120,
    },
  },
  ...sectionWrapperStyles(theme),
});

const AddNewItemDialog = (props) => {
  
  const {
    classes,
    fields,
    open,
    setOpenDialog,
    addNewAdministrationItem,
    checkIfCustomerCodeExists,
    page,
    type,
    machineId,
    setLoadingProgress,
    searchText,
    administrationExtraData,
    administrationListData,
    setAdvancedMode,
    advancedMode,
    vatOptions,
    dictionaries,
    renderAdvancedSwitch,
    setDirty,
    isDirty,
    setNewlyCreatedItemName,
  } = props;
  
  const rowsFields = sliceRows(fields.filter(fieldToFilter => advancedMode || fieldToFilter.basic));
  const [cancelPopup, setCancelPopup] = useState(false);
  const { formatMessage } = useIntl();
  
  const handleDialogPopup = () => {
    if (isDirty) {
      setCancelPopup(true);
    } else {
      setDirty(false);
      setOpenDialog(false);
    }
  };
  
  const handleCancelPopup = () => {
    setOpenDialog(true);
    setCancelPopup(false);
  };
  
  const handleConfirmPopup = () => {
    setCancelPopup(false);
    setOpenDialog(false);
    setDirty(false);
  };
  
  return (
    <>
      <Dialog className={classes.dialogWrapper} maxWidth="lg" open={open}
              onClose={() => handleDialogPopup()} aria-labelledby="form-dialog-title">
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <DialogTitle id="form-dialog-title">
            <FormattedMessage id="page.administration.addNewItem" defaultMessage="Add new item" />
          </DialogTitle>
      
          {renderAdvancedSwitch && <Box display="flex" alignItems="center" className={classes.advOptions}>
            <span><FormattedMessage id="page.administration.advancedOptions" defaultMessage="Advanced options" /></span>
            <Switch color="primary" checked={advancedMode} onChange={() => setAdvancedMode(!advancedMode)} />
          </Box>}
        </Box>
    
        <DialogContent className={classes.dialogContent}>
          <Formik
            enableReinitialize
            initialValues={fields.reduce((obj, field) =>
              Object.assign(obj,
                {
                  [field.name]: (field.initialValue || field.initialValue === 0)
                    ? field.initialValue
                    : getDefaultValueForFieldType(field.type),
                }), {})
            }
            onSubmit={(values, formikHelpers) => {
              if (type !== ADMINISTRATION_DATA_TYPE.GIFT_CARD_TABLE || (!values.CustomerCode || checkIfCustomerCodeExists(machineId, values, formikHelpers.setSubmitting, formikHelpers.setFieldError, setLoadingProgress))) {
                setOpenDialog(false);
                switch (type) {
                  default:
                    return addNewAdministrationItem(type, machineId, values, formikHelpers.setSubmitting, setNewlyCreatedItemName, page - 1, setLoadingProgress, searchText);
                }
              }
            }}>
            {({ isSubmitting, setFieldValue, values }) => {
              return (
                <Form>
  
                  <AdministrationItemsFormFields
                    values={values}
                    rows={rowsFields}
                    classes={classes}
                    administrationExtraData={administrationExtraData}
                    administrationListData={administrationListData}
                    setFieldValue={setFieldValue}
                    isUpdate={false}
                    setDirty={setDirty}
                    vatOptions={vatOptions}
                    dictionaries={dictionaries}
                  />
  
                  <div className={classes.buttonsWrapper}>
                    <CommonButton
                      color="secondary"
                      variant="outlined"
                      onClick={() => setOpenDialog(false)}
                      label={<FormattedMessage
                        id="button.cancel"
                        defaultMessage="Cancel"
                      />}
                    />
    
                    <CommonButton
                      variant="outlined"
                      type="submit"
                      label={<FormattedMessage
                        id="page.administration.addNew"
                        defaultMessage="Add"
                      />}
                      disabled={isSubmitting}
                      onClick={() => setDirty(false)}
                    />
                  </div>

                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
  
      {cancelPopup && isDirty && <CustomPrompt
        message={formatMessage({
          id: 'confirmation.prompt',
          defaultMessage: 'Are you sure you want to leave without saving?',
        })}
        cleanUp={handleConfirmPopup}
        onCancel={handleCancelPopup}
        cancelText={formatMessage({
          id: 'confirmation.cancel.text',
          defaultMessage: 'No',
        })}
        confirmText={formatMessage({
          id: 'confirmation.confirm.text',
          defaultMessage: 'Yes',
        })}
      />}
    </>
  );
};

const decorate = compose(
  withStyles(styles),
  connect(
    null,
    dispatch => bindActionCreators({
      addNewAdministrationItem,
    }, dispatch)),
);

AddNewItemDialog.propTypes = {
  open: PropTypes.bool,
  setOpenDialog: PropTypes.func,
  addNewAdministrationItem: PropTypes.func,
  page: PropTypes.number,
  machineId: PropTypes.string,
  setLoadingProgress: PropTypes.func,
  searchText: PropTypes.string,
  advancedMode: PropTypes.bool,
  administrationExtraData: PropTypes.object,
  administrationListData: PropTypes.object,
  fields: PropTypes.array.isRequired,
  classes: PropTypes.object,
  type: PropTypes.string.isRequired,
  checkIfCustomerCodeExists: PropTypes.func,
  vatOptions: PropTypes.array,
  fetchDictionaries: PropTypes.func,
  dictionaries: PropTypes.array,
  setDirty: PropTypes.func,
  isDirty: PropTypes.bool,
  setNewlyCreatedItemName: PropTypes.func,
};

export default decorate(AddNewItemDialog);